/**
 * /zh/best-knowledge-base-software — 知识库软件清单中文 SEO 页。与
 * BestKnowledgeBaseSoftware.tsx 同构，原生中文，hreflang 双向。
 */
import { MarketingShell } from './MarketingShell';

export function BestKnowledgeBaseSoftwareZh() {
  return (
    <MarketingShell
      currentLocale="zh"
      hreflangPath="/best-knowledge-base-software"
      documentTitle="知识库软件清单（2026）：按用途诚实排序 · Draftlize"
      documentDescription="一份不灌水的知识库软件清单（2026），按用途分类：团队 wiki、客服/帮助中心、开发者文档、开源自托管——每类点名真正最强的，而不是一堵返佣 logo 墙。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 860 }}>
          <span className="label label-amber">指南 · 知识库软件</span>
          <h1 className="serif" style={{ fontSize: 'clamp(36px, 4.6vw, 56px)', lineHeight: 1.08, margin: '12px 0 24px' }}>
            知识库软件清单，<br/>
            <em>按你到底在记什么来分类。</em>
          </h1>
          <p className="lede">
            「知识库软件」其实是四种不同的产品，取决于谁在问——团队 wiki、客户帮助中心、开发者文档站，还是自托管的私有库。这份清单按每一类，诚实地点名最强的那个。我们自己也做其中一类工具，但话说在前：要做 wiki、帮助中心或 API 文档，都有比我们更知名、更合身的选择，我们会直接指给你。Draftlize 只占这里一个很窄的格子——让知识库记下的<em>决策</em>不悄悄过时。下面是诚实的全景。
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">一句话</span>
            <h2 className="serif">按知识库<em>「为谁而记」来选。</em></h2>
          </div>
          <p>常见的错误，是想找一个「知识库」同时伺候团队 wiki、客户和工程师。它们的拉力方向不同。下面是每件事的赢家——以及那个几乎没人真正覆盖的格子。</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th>你在记什么</th>
              <th>强选项</th>
              <th className="ours">Draftlize 在哪</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>内部团队 wiki</td><td className="yes">Notion、Confluence、Slite、Slab</td><td className="ours no">不是 wiki</td></tr>
            <tr><td>客户 / 帮助中心</td><td className="yes">Zendesk Guide、Document360、Helpjuice</td><td className="ours no">不面向客户</td></tr>
            <tr><td>开发者 / API 文档</td><td className="yes">GitBook、ReadMe、Docusaurus</td><td className="ours partial">持有文档引用的决策</td></tr>
            <tr><td>开源 / 自托管</td><td className="yes">BookStack、Outline、Wiki.js</td><td className="ours no">托管，非自托管</td></tr>
            <tr><td>知道某篇文章何时过时了</td><td className="partial">—— 几乎没人真正负责 ——</td><td className="ours yes">这就是整个产品</td></tr>
            <tr><td>决策一改，标记受影响的文档</td><td className="no">以上都不行</td><td className="ours yes">依赖图 + 自动标 stale</td></tr>
          </tbody>
        </table>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">分门别类，<em>诚实地说。</em></h2>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">内部团队 wiki</h3>
            <p>
              <strong>Notion</strong>——多数团队的默认；灵活的页面与数据库，大家都已经在用。<br/>
              <strong>Confluence</strong>——企业级主力，和 Jira 深度集成，偏重。<br/>
              <strong>Slite</strong> / <strong>Slab</strong>——更干净、搜索优先，专为公司知识而生。<br/>
              <em>诚实地讲：</em>团队知识就该住在这里。Draftlize 不是 wiki，也不假装是。
            </p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">客户 / 帮助中心</h3>
            <p>
              <strong>Zendesk Guide</strong>——如果客服已经跑在 Zendesk 上，它是标配。<br/>
              <strong>Document360</strong>——专做公开/私有帮助中心，带版本管理。<br/>
              <strong>Helpjuice</strong> / <strong>Guru</strong>——强搜索、在工作流里就给答案。<br/>
              <em>诚实地讲：</em>为「减少工单、把答案推给客户」而造，不是 Draftlize 的活。
            </p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">开发者 / API 文档</h3>
            <p>
              <strong>GitBook</strong>——精致、带 Git 同步；做产品 + API 文档都好。<br/>
              <strong>ReadMe</strong>——带「试一试」控制台的交互式 API 参考。<br/>
              <strong>Docusaurus</strong>——开源、docs-as-code，占住静态站这条路。<br/>
              <em>诚实地讲：</em>它们负责把文档渲染出来。它们不做的，是知道某篇文档描述的那个决策后来变了。
            </p>
          </div>
          <div className="problem-col">
            <span className="num">IV</span>
            <h3 className="serif">开源 / 自托管</h3>
            <p>
              <strong>BookStack</strong>——简单、整洁的自托管 wiki，好部署。<br/>
              <strong>Outline</strong>——现代、快、搜索好；把团队知识放在你自己的基建上。<br/>
              <strong>Wiki.js</strong>——灵活，Markdown/所见即所得，支持多种鉴权后端。<br/>
              <em>诚实地讲：</em>当数据驻留或成本排除了 SaaS 时，这就是「免费知识库软件」的答案。Draftlize 是托管的。
            </p>
          </div>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">真正属于<em>我们的那一格。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            知识库存的是「你曾经知道什么」。它们都不做的那件事，是在某个记下的决策不再成立时告诉你——于是知识库慢慢被一堆自信、却悄悄错了的文章填满。这件窄活，就是 Draftlize 的全部。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">决策变成结构化卡片</h3>
            <p>知识库文章把决策埋在散文里——「我们选 Postgres 是因为……」。Draftlize 把那个决策捕成一张卡片：选择、理由、备选、依赖。可按 URL 和 ID 寻址，于是一篇文档或 spec 能引用那个确切的决策，而不是复述它、然后慢慢与它脱节。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">改一个，依赖它的标 stale</h3>
            <p>这是 wiki 做不到的。每张卡片都知道谁依赖它。回头改了 Postgres 那个决策，所有建立在它上面的文档、spec、runbook 自动标记 stale——就像构建系统让被改文件的下游全部失效一样。知识库不再是半真页面的坟场。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">你的 AI agent 能读也能写</h3>
            <p>wiki 留着写散文，帮助中心留给客户。Draftlize 是它们底下的决策层，Claude Code 或 Cursor 经 MCP 读写——于是回答「为什么是这么建的」的 agent，读到的是当前的决策，而不是一篇上线后没人更新过的 wiki 页。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          知识库记得你写下了什么，却说不出其中哪些还成立。
        </blockquote>
        <cite>留着你的 wiki、帮助中心、文档，补上那层会标记「什么过时了」的东西。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常见问题</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820, fontFamily: "'Inter', sans-serif" }}>
          {[
            { q: '知识库和 wiki 有什么区别？', a: 'wiki 是结构松散、协作编辑的一组页面——谁都能自由地加和链接；知识库通常更经过整理、更偏搜索优化，常常面向特定受众（给客户的客服 KB、给员工的内部 KB）。实际中两个词高度重叠，真正不同的是受众和整理程度。' },
            { q: '最好的免费或开源知识库软件是哪个？', a: '自托管且免费的，BookStack（最好部署）、Outline（现代且快）、Wiki.js（最灵活）最强。当数据驻留、成本或定制需求排除了 SaaS 时，它们是对的选择。如果你只想零配置，Notion 免费档就够小团队用。' },
            { q: '内部知识库和对外知识库是同一个工具吗？', a: '通常不是。内部 KB（Notion、Confluence、Slite）优化的是团队快速编辑和广度；对外 KB（Zendesk Guide、Document360、Helpjuice）优化的是公开发布、SEO、版本管理和减少工单。逼一个工具同时干两件事，往往两件都干不好。' },
            { q: '知识库能取代 PRD 或决策日志吗？', a: '不能——团队恰恰在这里栽跟头。知识库以散文形式记录知识，它根本不知道自己记下的某个决策何时变了，于是依赖它的文章悄悄腐烂。带真正依赖追踪的决策日志或 PRD 是另一回事。这道缝，正是 Draftlize 在你所用的任何 KB 底下要补的。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">补上决策层<em>——$5 免费起步。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新账号送 $5</span>
          <span>只为实际用量付费</span>
          <span>余额永不过期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            留着你已经在用的 wiki、帮助中心和文档。补上 Draftlize 来做它们都不做的那件事：确保它们记下的决策保持最新——并且你的 AI agent 读到的是「活」的那一版。
          </p>
          <a className="btn-primary" href="/">$5 免费开始</a>
        </div>
      </section>
    </MarketingShell>
  );
}
